import type { IntlShape, MessageDescriptor } from 'react-intl';

import type { AgentCoreActivity } from '@/shared/api/agentCore/agentCoreApi';

import { type ActivityItem, patientAgentMessages } from '../config';

export const presentActivity = (
  activity: AgentCoreActivity,
  formatMessage: IntlShape['formatMessage'],
): Pick<ActivityItem, 'label' | 'detail' | 'visual'> => {
  const completed = activity.state === 'completed';
  const labels: Record<
    AgentCoreActivity['kind'],
    [MessageDescriptor, MessageDescriptor]
  > = {
    working: [
      patientAgentMessages.activityWorking,
      patientAgentMessages.activityWorkCompleted,
    ],
    search_records: [
      patientAgentMessages.activitySearchingRecords,
      patientAgentMessages.activitySearchedRecords,
    ],
    read_report: [
      patientAgentMessages.activityReadingReport,
      patientAgentMessages.activityReadReport,
    ],
    read_consultation: [
      patientAgentMessages.activityReadingConsultation,
      patientAgentMessages.activityReadConsultation,
    ],
    compare_reports: [
      patientAgentMessages.activityComparingReports,
      patientAgentMessages.activityComparedReports,
    ],
    inspect_images: [
      patientAgentMessages.activityReviewingImages,
      patientAgentMessages.activityReviewedImages,
    ],
    list_documents: [
      patientAgentMessages.activityFindingDocuments,
      patientAgentMessages.activityFoundDocuments,
    ],
    read_document: [
      patientAgentMessages.activityReadingDocument,
      patientAgentMessages.activityReadDocument,
    ],
    create_document: [
      patientAgentMessages.activityCreatingDocument,
      patientAgentMessages.activityCreatedDocument,
    ],
    update_document: [
      patientAgentMessages.activityUpdatingDocument,
      patientAgentMessages.activityUpdatedDocument,
    ],
    analyze_patient_data: [
      patientAgentMessages.activityAnalyzingData,
      patientAgentMessages.activityAnalyzedData,
    ],
  };
  const fallbackPair: [MessageDescriptor, MessageDescriptor] = [
    patientAgentMessages.activityWorking,
    patientAgentMessages.activityWorkCompleted,
  ];
  // Unknown backend kinds fall back to the generic working label instead of
  // crashing the timeline render.
  const pair = labels[activity.kind] ?? fallbackPair;
  // A failed activity must not keep its present-tense "in progress" label
  // forever; the timeline marks the visual as error, the label says so too.
  if (activity.state === 'failed') {
    return {
      label: formatMessage(patientAgentMessages.activityFailed, {
        label: formatMessage(pair[0]),
      }),
      detail: '',
      visual: 'error',
    };
  }
  return {
    label: formatMessage(pair[completed ? 1 : 0]),
    detail: '',
    visual:
      activity.state === 'running'
        ? 'progress'
        : activity.kind === 'inspect_images'
          ? 'image'
          : 'check',
  };
};
